import { lightTheme } from "./theme.mjs";
import { transitionStyle } from "./styles.mjs";
import { useTheme } from "./ThemeProvider.mjs";
import { style, useStyles } from "purse-styles";
import React, { createContext, useContext } from "react";
import { mapValues } from "@repo/utils/objectUtils";
import { createStyleObject } from "@capsizecss/core";
import interMetrics from "@capsizecss/metrics/inter";
import newsreaderMetrics from "@capsizecss/metrics/newsreader";
import { Focusable } from "react-aria-components";
import { jsx } from "react/jsx-runtime";

//#region src/Text.tsx
const capsizeStylesCache = {
	Inter: mapValues(lightTheme.fontStyles, (config) => {
		const capsizeStyles = createStyleObject({
			fontSize: config.fontSize,
			leading: parseInt(config.lineHeight) - config.fontSize,
			fontMetrics: interMetrics
		});
		return style({
			fontSize: capsizeStyles.fontSize,
			lineHeight: capsizeStyles.lineHeight,
			"&::after": capsizeStyles["::after"],
			"&::before": capsizeStyles["::before"]
		});
	}),
	Newsreader: mapValues(lightTheme.fontStyles, (config) => {
		const capsizeStyles = createStyleObject({
			fontSize: config.fontSize,
			leading: parseInt(config.lineHeight) - config.fontSize,
			fontMetrics: newsreaderMetrics
		});
		return style({
			fontSize: capsizeStyles.fontSize,
			lineHeight: capsizeStyles.lineHeight,
			"&::after": capsizeStyles["::after"],
			"&::before": capsizeStyles["::before"]
		});
	})
};
const emptyStyle = style({});
function textStylesToCss(props, fontStyles, theme) {
	return removeUndefined({
		fontSize: props.size ? fontStyles[props.size].fontSize : void 0,
		lineHeight: props.size ? fontStyles[props.size].lineHeight : void 0,
		letterSpacing: props.size ? fontStyles[props.size].letterSpacing : void 0,
		fontFamily: props.font ? theme.fontFamilies[props.font] : void 0,
		fontWeight: props.weight ? theme.textWeights[props.weight] : void 0,
		textWrap: props.balance ? "balance" : void 0,
		color: props.color ? theme.colors[props.color] : void 0,
		textAlign: props.align,
		fontStyle: props.italic ? "italic" : void 0,
		...props.style
	});
}
const TextStylesContext = createContext({
	size: "md",
	color: "sand12",
	weight: "normal",
	font: "Inter",
	select: void 0
});
TextStylesContext.displayName = "TextStylesContext";
function removeUndefined(object) {
	return Object.fromEntries(Object.entries(object).filter(([_, value]) => value !== void 0));
}
const TextStylesProvider = ({ children, ...props }) => {
	const ancestorStyles = useContext(TextStylesContext);
	return /* @__PURE__ */ jsx(TextStylesContext.Provider, {
		value: {
			...ancestorStyles,
			...removeUndefined(props)
		},
		children
	});
};
function useTextStyles({ truncate = false, light, color: propColor = light ? "sand11" : void 0, mobile, tablet, ...propStyles }) {
	const theme = useTheme();
	const { size, style: style$1, weight, color: colorName, align, balance, italic, font, capsize, shimmer, select, transition } = {
		...useContext(TextStylesContext),
		...propStyles,
		...propColor ? { color: propColor } : {}
	};
	const fontStyle = italic ? "italic" : void 0;
	const fontStyles = theme.fontStyles;
	const isMultiLineTruncate = typeof truncate === "number";
	const isSingleLineTruncate = truncate === true;
	const textOverflow = isSingleLineTruncate || isMultiLineTruncate ? "ellipsis" : void 0;
	const whiteSpace = isSingleLineTruncate ? "nowrap" : void 0;
	const overflow = isSingleLineTruncate || isMultiLineTruncate ? "hidden" : void 0;
	const display = isMultiLineTruncate ? "-webkit-box" : void 0;
	const WebkitLineClamp = isMultiLineTruncate ? `${truncate}` : void 0;
	const WebkitBoxOrient = isMultiLineTruncate ? "vertical" : void 0;
	const capsizeStyles = capsize && size && font ? capsizeStylesCache[font][size] : void 0;
	const originalColor = colorName ? theme.colors[colorName] : theme.colors.sand12;
	const shimmerBaseColor = shimmer ? `oklch(from ${originalColor} calc(l - 0.15) c h / 0.80)` : void 0;
	const shimmerHighlightColor = shimmer ? `oklch(from ${originalColor} calc(l + 0.15) c calc(h - 15) / 1.0)` : void 0;
	return `${useStyles(capsizeStyles ? capsizeStyles : emptyStyle, transition ? transitionStyle({
		transition,
		theme
	}) : {}, {
		fontSize: capsizeStyles ? void 0 : size ? fontStyles[size].fontSize : void 0,
		lineHeight: capsizeStyles ? void 0 : size ? fontStyles[size].lineHeight : void 0,
		letterSpacing: size ? fontStyles[size].letterSpacing : void 0,
		fontFamily: font ? theme.fontFamilies[font] : void 0,
		fontWeight: weight ? theme.textWeights[weight] : void 0,
		textWrap: balance ? "balance" : void 0,
		color: shimmer ? "transparent" : colorName ? theme.colors[colorName] : void 0,
		textAlign: align,
		fontStyle,
		textOverflow,
		whiteSpace,
		overflow,
		display,
		WebkitLineClamp,
		WebkitBoxOrient,
		margin: 0,
		background: shimmer && shimmerBaseColor && shimmerHighlightColor ? `linear-gradient(-67deg, ${shimmerBaseColor} 0%, ${shimmerBaseColor} 42%, ${shimmerHighlightColor} 50%, ${shimmerBaseColor} 58%, ${shimmerBaseColor} 100%)` : void 0,
		backgroundSize: shimmer ? "200% 100%" : void 0,
		backgroundPosition: shimmer ? "100% 0" : void 0,
		WebkitBackgroundClip: shimmer ? "text" : void 0,
		backgroundClip: shimmer ? "text" : void 0,
		animation: shimmer ? "shimmer 1.65s linear infinite" : void 0,
		userSelect: select === false ? "none" : void 0,
		cursor: select === false ? "default" : void 0
	}, style$1 ? style$1 : emptyStyle)} ${useStyles(tablet ? { [`@media (max-width: ${theme.breakpoints.tablet}px)`]: textStylesToCss(tablet, fontStyles, theme) } : {})} ${useStyles(mobile ? { [`@media (max-width: ${theme.breakpoints.mobile}px)`]: textStylesToCss(mobile, fontStyles, theme) } : {})}`.trim();
}
const Text = React.forwardRef(({ as: elementName = "span", children, className: classNameProp = "", role, "aria-level": ariaLevel, flex, basis, grow, shrink, alignSelf, focusable, ...propStyles }, ref) => {
	const className = useTextStyles(propStyles);
	const flexClassName = useStyles({
		flex: flex === true ? 1 : flex,
		flexBasis: basis,
		flexGrow: grow === true ? 1 : grow,
		flexShrink: shrink === true ? 1 : shrink,
		alignSelf
	});
	const element = React.createElement(elementName, {
		className: className + " " + flexClassName + " " + classNameProp,
		role,
		"aria-level": ariaLevel,
		ref
	}, typeof children === "string" ? children : /* @__PURE__ */ jsx(TextStylesProvider, {
		...propStyles,
		children
	}));
	if (focusable) return /* @__PURE__ */ jsx(Focusable, { children: element });
	return element;
});

//#endregion
export { Text, TextStylesProvider, useTextStyles };